import { createTheme, defineThemeOverride } from './create-theme';

/**
 * Luxury example theme (warm ivory / charcoal palette).
 * Colors from Figma Brand Guidelines:
 * https://www.figma.com/design/4QacUEdl0xov8wmSZ1bvL7/Metropolis-Recognition-Platform--MRP-?node-id=310-36135
 *
 * Ivory/000 #F9F7F2 · Ivory/200 #EAE5DB · Ivory/300 #DED8CC · Ivory/400 #CDC6B8
 * Ivory/700 #45423D · Ivory/900 #1A1917
 */
/** Override config — also used by the Semantic Tokens Storybook matrix. */
export const luxuryThemeConfig = defineThemeOverride({
  theme: {
    tokens: {
      colors: {
        ivory: {
          '000': { value: '#F9F7F2' },
          100: { value: '#F3F0E8' },
          200: { value: '#EAE5DB' },
          300: { value: '#DED8CC' },
          400: { value: '#CDC6B8' },
          500: { value: '#A89F8E' },
          600: { value: '#6B655C' },
          700: { value: '#45423D' },
          800: { value: '#2A2825' },
          900: { value: '#1A1917' },
        },
        // Warm neutrals so gray-based recipes/semantics pick up the ivory palette.
        gray: {
          '000': { value: '#FFFFFF' },
          100: { value: '#F9F7F2' },
          200: { value: '#EAE5DB' },
          300: { value: '#DED8CC' },
          400: { value: '#CDC6B8' },
          500: { value: '#9E9E9E' },
          600: { value: '#45423D' },
          700: { value: '#2A2825' },
          800: { value: '#1A1917' },
          900: { value: '#1A1917' },
        },
        // Charcoal brand instead of Metropolis purple (switches, pay/brand accents).
        brand: {
          '000': { value: '#F9F7F2' },
          100: { value: '#EAE5DB' },
          200: { value: '#DED8CC' },
          300: { value: '#6B655C' },
          400: { value: '#333D33' },
          500: { value: '#2A2825' },
          600: { value: '#1A1917' },
          700: { value: '#0D0C0B' },
        },
      },
    },
    semanticTokens: {
      colors: {
        text: {
          primary: {
            value: { _light: '{colors.ivory.900}', _dark: '{colors.ivory.000}' },
          },
          secondary: {
            value: { _light: '{colors.ivory.700}', _dark: '{colors.ivory.400}' },
          },
          tertiary: {
            value: { _light: '{colors.ivory.500}', _dark: '{colors.ivory.500}' },
          },
        },
        fill: {
          background: {
            default: {
              value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.900}' },
            },
            'light-gray': {
              value: { _light: '{colors.ivory.100}', _dark: '{colors.ivory.800}' },
            },
          },
          button: {
            secondary: {
              enabled: {
                value: { _light: '{colors.ivory.200}', _dark: '{colors.ivory.700}' },
              },
              hover: {
                value: { _light: '{colors.ivory.300}', _dark: '{colors.ivory.600}' },
              },
              active: {
                value: { _light: '{colors.ivory.400}', _dark: '{colors.ivory.500}' },
              },
            },
          },
        },
        stroke: {
          divider: {
            default: {
              value: { _light: '{colors.ivory.200}', _dark: '{colors.ivory.700}' },
            },
          },
        },
        border: {
          subtle: {
            value: { _light: '{colors.ivory.300}', _dark: '{colors.ivory.600}' },
          },
        },
      },
    },
  },
});

export const luxurySystem = createTheme(luxuryThemeConfig);
